import Splide from '@splidejs/splide';
import { createStore, produce } from 'solid-js/store';
import { render as renderSolidJS } from 'solid-js/web';
import { fromEvent, tap } from 'rxjs';
import HeaderMobileNavigationBreadcrumb from 'components/reactive/header/navigation/breadcrumb';
import FullModal from 'utils/components/full-modal';

export default (node: HTMLElement, CLASSES: { HEADER_OVERLAY_ACTIVE: string }) => {
  const trigger = node.querySelector('[data-mobile-menu-trigger]');
  const container = node.querySelector('[data-target="mobile-navigation"]');

  const initLogic = () => {
    const carousel = new Splide(container.querySelector<HTMLElement>('[data-carousel]'), {
      arrows: false,
      pagination: false,
      drag: false,
      speed: 1000,
    }).mount();

    const [breadcrumbList, setBreadcrumbList] = createStore<{ title: string; handle: string }[]>(
      [],
    );

    renderSolidJS(
      () => (
        <HeaderMobileNavigationBreadcrumb
          class="mb-5 mt-2"
          list={breadcrumbList}
          carousel={carousel}
        />
      ),
      node.querySelector('[data-breadcrumb]'),
    );

    carousel.on('moved', (active) => {
      // Remove "next" elements when going back
      carousel.remove((slide) => slide.index > active);
    });

    carousel.on('move', (active, prevActive) => {
      const realIndex = active / 2 - 1;

      // Remove "next elements when going back
      setBreadcrumbList(
        produce((list) => {
          // @ts-ignore
          list.splice(realIndex + 1);
        }),
      );

      // Add transition fade effect
      carousel.Components.Slides.getAt(active).slide.classList.remove('opacity-0');
      carousel.Components.Slides.getAt(prevActive).slide.classList.add('opacity-0');
    });

    const addBreadcrumb = (title: string, handle: string) => {
      setBreadcrumbList(
        produce((list) => {
          // @ts-ignore
          list.push({ title, handle });
        }),
      );
    };

    const listClickEvent = (root: Element) =>
      fromEvent(root.querySelectorAll<HTMLElement>('[data-child]'), 'click').pipe(
        tap((event) => {
          event.preventDefault();
        }),
      );

    const handleNavigation = (event: Event) => {
      const { currentTarget: link } = event;

      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }

      addBreadcrumb(link.ariaLabel ?? link.textContent.trim(), link.dataset.title);

      const list = container
        .querySelector<HTMLTemplateElement>(`template#${link.dataset.child}`)
        .content.firstElementChild.cloneNode(true) as HTMLElement;

      list.classList.add('shrink-0', 'overflow-auto', 'h-0', 'grow');

      carousel.Components.Slides.add(document.createElement('li')); // Spacer element

      const wrapper = document.createElement('div');
      wrapper.classList.add('flex', 'flex-col', 'h-full');
      wrapper.append(list);

      let menuImages = null;

      if (breadcrumbList.length === 1) {
        menuImages = container.querySelector<HTMLTemplateElement>(
          `template#images-${breadcrumbList[0].handle}-2`,
        );
      } else if (breadcrumbList.length === 2) {
        menuImages = container.querySelector<HTMLTemplateElement>(
          `template#images-${breadcrumbList[0].handle}-${breadcrumbList[1].handle}`,
        );
      }

      if (menuImages) {
        wrapper.append(menuImages.content.firstElementChild.cloneNode(true));
      }

      const listItem = document.createElement('li');
      listItem.classList.add('opacity-0', 'duration-1000', 'overflow-auto');
      listItem.append(wrapper);

      carousel.Components.Slides.add(listItem);
      carousel.go(carousel.length - 1);

      listClickEvent(list).subscribe(handleNavigation);
    };

    listClickEvent(container).subscribe(handleNavigation);
  };

  const initOpenable = () => {
    const navigation = new FullModal(container.querySelector('[data-mobile-navigation]'), {
      overlay: {
        index: 20,
      },
    });

    fromEvent(trigger, 'click').subscribe(() => {
      navigation.toggle();
    });

    fromEvent(navigation.root.querySelectorAll('[data-close]'), 'click').subscribe(() => {
      navigation.close();
    });

    navigation.on('open', () => {
      node.setAttribute('data-overlay-element', 'navigation');
      node.classList.add(CLASSES.HEADER_OVERLAY_ACTIVE);
      document.body.style.overflow = 'hidden';
    });

    navigation.on('close', () => {
      node.removeAttribute('data-overlay-element');
      node.classList.remove(CLASSES.HEADER_OVERLAY_ACTIVE);
      document.body.style.overflow = null;
    });
  };

  initOpenable();
  initLogic();
};
